import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import '../styles/MCQPractice.css';
import { useSelector } from 'react-redux';
import Loader from '../../../components/utils/Loader';
import Tooltip from '../../../components/utils/Tooltip';

const PracticeHistory = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const [selectedSetId, setSelectedSetId] = useState('');
    const [setMasterList, setSetMasterList] = useState([]);
    const [practicedSetList, setPracticedSetList] = useState([]);
    const [selectedPracticeSetId, setSelectedPracticeSetId] = useState('');

    const [practicedMaster, setPracticedMaster] = useState([]);
    const [isRepeated, setIsRepeated] = useState(0);

    //MyPracticedSetList
    const fetchPracticedSetList = useCallback(() => {
        const config = {
            url: "/mypracticedsets",
            method: "get",
            headers: { Authorization: authState.token}
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.practicedsetlist)) {
                    setPracticedSetList(response.practicedsetlist);
                } else {
                    console.error("Invalid response format: Practice set list not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching practice list:", error);
            });
    }, [authState.token, fetchData]);

    useEffect(() => {
        fetchPracticedSetList();
    }, [fetchPracticedSetList]);

    const sortedPracticedSetList = [...practicedSetList].sort((a, b) => new Date(b.datePracticed) - new Date(a.datePracticed));

    const handlePracticeSetView = (setId) => {
        setSelectedPracticeSetId(prevId => (prevId === setId ? null : setId));
    };

    function convertTimeLimit(minutes) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        const formattedTimeLimit = `${hours}:${remainingMinutes} (Hr:Min)`;
        return formattedTimeLimit;
    }

    function convertTimeTaken(milliseconds) {
        const hours = Math.floor(milliseconds / 3600000);
        const remainingMinutes = Math.floor((milliseconds % 3600000) / 60000);
        const remainingSeconds = Math.floor((milliseconds % 60000) / 1000);
        const remainingMilliseconds = milliseconds % 1000;
        const formattedTime = `${hours}:${remainingMinutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds
                .toString()
                .padStart(2, "0")}.${remainingMilliseconds
                    .toString()
                    .padStart(3, "0")} (Hr:Min:Sec)`;
        return formattedTime;
    }

    const formatDateTime = (date) => {
        const options = {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        };
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };


    return (
       <div className="min-h-screen bg-black py-2">
            <div className='border-b border-blue-400 mb-2'>
                <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 mb-2 p-2">
                    📖 My Learning Practiced List
                </h2>
            </div>
            <div className="min-h-[80vh] bg-black mt-2 p-4">
                {loading ? (
                    <div className="flex justify-center items-center py-8">
                    <Loader />
                    </div>
                ) : (
                    <div className="space-y-4">
                    <h2 className="text-lg lg:text-xl font-bold text-green-600 mb-4">
                        My Practice List
                    </h2>

                    {practicedSetList && practicedSetList.length === 0 ? (
                        <div className="flex items-center justify-center w-full h-64 text-yellow-200">
                        No records found!
                        </div>
                    ) : (
                        sortedPracticedSetList.map((practicedSet, index) => (
                        <div
                            key={practicedSet._id}
                            className="bg-black border border-gray-500 p-4 rounded-lg shadow-sm"
                        >
                            <div className="flex items-center justify-between">
                            <p className="font-semibold text-yellow-200">
                                {index + 1}. {practicedSet.practiceTitle}
                            </p>
                            <Tooltip text={"View details"} position={"top"}>
                                <span
                                className="text-blue-500 cursor-pointer hover:text-blue-700 transition"
                                onClick={() => handlePracticeSetView(practicedSet._id)}
                                >
                                <i className="fa-solid fa-eye"></i>
                                </span>
                            </Tooltip>
                            </div>
                            <p className="text-white">Date: {formatDateTime(practicedSet.datePracticed)}</p>
                            {selectedPracticeSetId === practicedSet._id && (
                            <div className="bg-black text-white mt-4 p-4 rounded-lg">
                                <h2 className="text-xl font-bold text-yellow-200 mb-2">
                                {practicedSet.practiceTitle}
                                </h2>
                                <div className="space-y-2 mb-4 text-white">
                                <p><strong>Date Practiced:</strong> {new Date(practicedSet.datePracticed).toLocaleDateString()}</p>
                                <p><strong>Is Repeated:</strong> {practicedSet.isRepeatPractice === 1 ? 'Yes' : 'No'}</p>
                                <p><strong>Time Limit:</strong> {convertTimeLimit(practicedSet.timeLimit)}</p>
                                <p><strong>Time Taken:</strong> {convertTimeTaken(practicedSet.timeTaken)}</p>
                                <p><strong>Number of Questions:</strong> {practicedSet.numberOfQuestions}</p>
                                <p><strong>Correct Answers:</strong> {practicedSet.correctAnswers}</p>
                                <p><strong>Full Marks:</strong> {practicedSet.fullMarks}</p>
                                <p><strong>Marks Obtained:</strong> {practicedSet.marksObtained}</p>
                                <p><strong>Percentage:</strong> {practicedSet.percentage}%</p>
                                <div className="relative pt-1 text-white">
                                    <div className="flex mb-2 items-center justify-between">
                                    <span className="text-sm font-medium text-white">Progress</span>
                                    <span className="text-sm font-medium text-white">{practicedSet.percentage}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500"
                                            style={{ width: `${practicedSet.percentage}%` }}
                                        >
                                        </div>
                                    </div>
                                </div>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-yellow-200">Answers:</h3>
                                    {practicedSet.answers.map((answer, index) => (
                                        <div key={index} className="p-2 border text-sm lg:text-base  border-gray-300 rounded-lg">
                                            <p><strong>Question {index + 1}:</strong> {answer.questionTitle}</p>
                                            <p className={answer.marksObtained <= 0 ? 'text-red-500' : 'text-gray-100'}>
                                                <strong>My Answer:</strong> {answer.userMCQAnswer}
                                            </p>
                                            <p className={answer.marksObtained <= 0 ? 'text-red-500' : 'text-gray-200'}>
                                                <strong>Marks Obtained:</strong> {answer.marksObtained}
                                            </p>
                                            <p><strong>Marks for Correct Answer:</strong> {answer.correctAnswerPoints}</p>
                                            {answer.isMCQCorrect === 0 && (
                                                <p><strong>Marks for Wrong Answer:</strong> {answer.wrongAnswerPoints}</p>
                                            )}
                                            {answer.remarks && <p><strong>Remarks:</strong> {answer.remarks}</p>}
                                            {answer.userShortAnswer && <p><strong>My Short Answer:</strong> {answer.userShortAnswer}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            )}
                        </div>
                        ))
                    )}
                    </div>
                )}
            </div>
            <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
                <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
                    <div className="flex flex-wrap gap-4 text-sm">
                        <Link to="/about-us" className="hover:text-teal-300">About</Link>
                        <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                        <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                        <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
                    </div>
                </div>
            </footer>  
        </div>
    );
};

export default PracticeHistory;
